/**
 * useOfflineRecordingEvents — subscribes to all offline_recording:* events from
 * Rust, updates Jotai atoms.
 *
 * Purely reactive: no business logic, just atom updates and cache invalidations.
 * Self-guards with isTauri() — safe to mount at app root unconditionally.
 */
import { listen, type Event, type UnlistenFn } from '@tauri-apps/api/event';
import { getCurrentWindow } from '@tauri-apps/api/window';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { createElement, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { offlinePatientLinksBySessionIdAtom } from '@/atoms/offline-recording';
import { getCachedLastUsedRecorderSettings } from '@/atoms/offline-recording/cached-recorder-settings';
import {
  acknowledgeListedHandovers,
  forgetOfflineHandover,
} from '@/atoms/offline-recording/offline-handover';
import {
  offlineActiveRecordingIdAtom,
  offlineCapturePausedAtom,
  offlineConnectivityStateAtom,
  offlineDeletedLocalSessionIdsAtom,
  offlineDismissedSyncedBadgeIdsAtom,
  offlineLocalSessionsAtom,
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
  offlinePendingServerDeleteIdsAtom,
  offlineSyncQueueAtom,
  reconcilePendingServerDeleteIds,
  unionServerSessionIds,
  withoutServerSessionId,
  type OfflineLocalSessionListItem,
} from '@/atoms/offline-recording/sync';
import {
  getOfflineRecordingErrorMessage,
  offlineRecordingErrorMessages,
} from '@/components/offline-recording/error-messages';
import { GET_SESSIONS_LIST } from '@/constants/sessions';
import { useBuildSessionPath } from '@/features/sessions/utils/buildSessionPath';
import { toSyncItem } from '@/hooks/desktop/offline-sync-queue-item';
import {
  NOTE_UNAVAILABLE_RETRY_REFRESH_REASON,
  registerOfflineSyncQueueRefresh,
} from '@/hooks/desktop/offline-sync-queue-refresh';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { invalidateTranscriptBySessionId } from '@/hooks/query/useQueryTranscriptBySessionId';
import {
  getUserStorageScope,
  userScopedStorageRehydratedAtom,
} from '@/lib/jotai-storage/user-scoped';
import type { OfflineMicrophoneSource } from '@/models/analytics/features/offline-recording';
import { restoreIdleStreamAfterCapture } from '@/tauri/audio-recorder/idle-stream-handover';
import { launchDesktopAuthLogin } from '@/tauri/auth0/login';
import { offlineDeviceLostMessages } from '@/tauri/ipc.messages';
import { OFFLINE_CONNECTIVITY_CHANGED_EVENT } from '@/tauri/offline-recording/connectivity';
import {
  OFFLINE_SERVER_DELETE_ENQUEUED_EVENT,
  OFFLINE_SERVER_DELETE_SETTLED_EVENT,
  offlineRecordingBridge,
  type OfflineServerDeleteEnqueuedEvent,
  type OfflineServerDeleteSettledEvent,
} from '@/tauri/offline-recording/recording';
import { isTauri } from '@/tauri/utils';
import type {
  OfflineConnectivityState,
  OfflineSyncItem,
} from '@/types/offline-recording';
import { isOfflineFailedStatus } from '@/types/offline-recording';
import { logSessionPipelineDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import {
  trackOfflineNoteGenerationFailed,
  trackOfflineRecordingDeleted,
  trackOfflineRecordingQueued,
  trackOfflineRecordingStarted,
  trackOfflineSessionDeleteReplayFailed,
  trackOfflineSyncCompleted,
  trackOfflineSyncFailed,
} from '@/utils/analytics/track-offline-recording-events';
import { logger } from '@/utils/logger/logger';
import { buildOfflineLocalSessionStub } from '@/utils/offline-recording/offlineSessionStub';
import { recordedSecondsFromDurationMs } from '@/utils/offline-recording/queuedRecordedDuration';
import { toTelemetryErrorCode } from '@/utils/offline-recording/telemetryErrorCode';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast, ToastActionButton } from '@heidi/design-system/toast';
import { MAIN_WINDOW_LABEL } from '@heidi/plugin-core';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
  GET_SESSION_BY_ID,
  GET_SESSIONS,
  GET_SESSIONS_LIST_FIRST_PAGE,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import { invalidateSessionById, notFoundSessionIds } from '@heidi/session/data';

// Copy for the sign-in action on the auth_required toast. Kept local because
// it's the only user-facing string this reactive hook owns; the toast
// title/description descriptors live in error-messages.ts.
const signInActionMessages = defineMessages({
  signIn: {
    defaultMessage: 'Sign in',
    id: '0Vy9mnnc1P',
    description: 'Action button on the offline sync toast prompting re-authentication',
  },
});

const OFFLINE_RECORDING_ANALYTICS_SOURCE = 'offline_recording' as const;

/**
 * Backoff for retrying the ONE hydration that rebuilds everything durable about
 * offline consults on a cold boot.
 *
 * That call is `offline_recording_list`, and until this it was single-shot: a
 * rejection logged a warning and nothing tried again, because every other
 * refresh is triggered by an event and a cold boot with no capture in progress
 * emits none. The queue then stayed empty for the whole run of the app, which is
 * not a cosmetic loss — the queue is what re-seeds the detail-cache stub for a
 * consult that only exists locally. No stub means the consult's own detail route
 * falls through to a server that has never heard of it, so a consult recorded
 * offline yesterday is simply unreachable today.
 *
 * The command can reject for reasons that pass on their own. It resolves the
 * caller's `user_id` from the cached identity and refuses without one, so
 * hydration racing whatever makes that identity readable loses the queue for the
 * session — a race whose outcome depends on machine speed, which is exactly the
 * kind of defect that reproduces on one laptop and nowhere else.
 *
 * Retried only on REJECTION, never on an empty list: an account with no offline
 * consults resolves normally, so the happy path still makes exactly one call and
 * this costs nothing fleet-wide. Bounded at three retries — this is a local
 * SQLite read, so a failure that survives ~6s is not a race and more attempts
 * would only delay the log that says so.
 */
const HYDRATION_RETRY_DELAYS_MS = [500, 1_500, 4_000] as const;

/**
 * How long the "couldn't be deleted" notice waits to see whether more queued
 * deletes are giving up in the same pass.
 *
 * The replay settles one row per request, so a clinician who deleted eight
 * sessions and lost permission on all eight would get eight identical toasts.
 * They are owed the fact once. Short enough that it is still the same moment
 * for the reader, long enough to cover a drain working through its rows.
 */
const DELETE_FAILED_TOAST_COALESCE_MS = 750;

// ---------------------------------------------------------------------------
// Event payload shapes (mirror Rust serde camelCase output)
// ---------------------------------------------------------------------------

interface CaptureStartedPayload {
  localSessionId: string;
  captureRegion: string;
  microphoneSource: OfflineMicrophoneSource;
  /**
   * Which recording of the consult this capture is — 0 for a first one, 1 or
   * more for a resume.
   *
   * The resume rate measured when it happens, rather than only for consults
   * that survive to `synced`. A resumed consult that is deleted, expires under
   * retention, or goes `unrecoverable` never reaches the sync-side
   * `recordingCount`, and those are disproportionately the interesting ones — a
   * two-part encounter is by definition one where something interrupted the
   * clinician.
   */
  takeIndex?: number;
}

interface CaptureEndedPayload {
  localSessionId: string;
}

interface PendingSyncPayload {
  localSessionId: string;
  fileSizeBytes: number;
  recordedDurationMs: number;
  captureRegion: string;
}

interface ConnectivityChangedPayload {
  state: OfflineConnectivityState;
}

// offline_recording:* events emitted by the sync orchestrator (APP-6665). These
// are already scoped to an offline recording on the Rust side (the orchestrator
// only emits for tasks in its in-flight map), so the FE needs no cross-talk
// guard — unlike the shared session_engine:* channel.
// offline_recording:status_changed — the orchestrator's authoritative
// sync-lifecycle transition (creating_session → uploading_audio → sync_failed /
// upload_failed). Rust persists the same value to `sync_tracking.status` in the
// same call, so the durable DB status and this queue atom cannot diverge.
// `lastErrorCode` is omitted on the wire for success transitions
// (skip_serializing_if on the Rust side), so it's optional here.
interface OfflineStatusChangedPayload {
  localSessionId: string;
  status: OfflineSyncItem['status'];
  attemptCount?: number | null;
  captureRegion?: string;
  lastErrorCode?: string | null;
}

interface OfflineSyncedPayload {
  localSessionId: string;
  sessionId?: string | null;
  attemptCount?: number | null;
  recordedDurationMs?: number | null;
  fileSizeBytes?: number | null;
  captureRegion?: string;
  /**
   * How many recordings this consult synced as one note.
   *
   * The only pre-rollout visibility into whether clinicians actually record a
   * consult in more than one part — a question nothing else can answer, since
   * the queue presents a multi-part consult as a single row.
   */
  recordingCount?: number | null;
  /**
   * Whether this consult synced carrying the instant it was recorded, or fell
   * back to the creation time the backend stamps when the sync runs.
   *
   * The only field signal that the local-recording-time fix is working. Its
   * failure is silent by construction — a dropped instant produces no error,
   * the PATCH succeeds, and the session simply shows the wrong time.
   *
   * `null`/absent means Rust could not read the session row at all, which is
   * not the same answer as `false` ("was not stamped").
   */
  hadLocalRecordingTime?: boolean | null;
}

interface OfflineNoteFailedPayload {
  localSessionId: string;
  errorCode?: string | null;
}

interface RecoveredPayload {
  recoveredSessionIds: string[];
}

interface DeletedPayload {
  localSessionId: string;
  reason: 'manual' | 'retention_purge' | 'abandoned_shell_gc';
}

/** The capture's microphone was unplugged and capture continued on another. */
interface InputRecoveredPayload {
  localSessionId: string;
  /** The microphone that dropped. */
  lostDevice: string;
  /** The one capture continued on. */
  deviceName: string;
}

/**
 * The capture's microphone was unplugged and could not be replaced. The capture
 * is still alive — Rust leaves it running rather than tearing it down — so the
 * clinician can end the session and keep everything captured before the unplug.
 */
interface InputRecoveryFailedPayload {
  localSessionId: string;
  lostDevice: string;
  /** `no_input_device` is actionable (plug one in); `error` is not. */
  reason: 'no_input_device' | 'error';
}

function mergeOfflinePatientLinks(
  prev: Record<string, string>,
  items: Array<{
    localSessionId: string;
    serverSessionId?: string | null;
    patientLink?: string | null;
  }>
): Record<string, string> {
  let next = prev;
  for (const item of items) {
    const patientLink = item.patientLink?.trim();
    if (!patientLink) continue;

    const ids = [item.localSessionId, item.serverSessionId].filter((id): id is string =>
      Boolean(id)
    );
    for (const id of ids) {
      if (next[id] === patientLink) continue;
      if (next === prev) next = { ...prev };
      next[id] = patientLink;
    }
  }
  return next;
}

// ---------------------------------------------------------------------------
// Queue-update helpers — module-level so handler nesting stays within 4 levels
// ---------------------------------------------------------------------------

function patchQueueItem(
  prev: OfflineSyncItem[],
  taskId: string,
  patch: Partial<OfflineSyncItem>
): OfflineSyncItem[] {
  return prev.map((item) =>
    item.localSessionId === taskId ? { ...item, ...patch } : item
  );
}

function removeQueuedSession(
  prev: OfflineSyncItem[],
  localSessionId: string
): OfflineSyncItem[] {
  return prev.filter((item) => item.localSessionId !== localSessionId);
}

/**
 * Fold a durable `list()` snapshot into the in-memory queue instead of
 * replacing it outright.
 *
 * Rust is authoritative for every row it still holds, so a snapshot row always
 * wins over the in-memory copy of the same recording. The one thing a snapshot
 * cannot speak for is a recording whose durable rows Rust has *already deleted*:
 * `finalize_synced` drops the `sync_tracking` rows the moment a sync completes,
 * so such a recording is simply absent from the snapshot. The renderer keeps a
 * temporary `synced` row for it on purpose — see `onSynced` — so the synthetic
 * list row does not blink out during the invalidate/refetch gap, and
 * `clearVisibleSyncedQueueItems` is what removes it once the backend session
 * shows up. Carrying that row across a hydration is the difference between a
 * spinner turning into a tick and a row vanishing and coming back.
 *
 * Order is preserved from `prev` for rows both sides know about, so a hydration
 * never reshuffles the list underneath the user; rows only the snapshot knows
 * about are appended in snapshot order.
 */
function reconcileQueueWithDurableList(
  prev: OfflineSyncItem[],
  next: OfflineSyncItem[]
): OfflineSyncItem[] {
  const nextById = new Map(next.map((item) => [item.localSessionId, item]));
  const reconciled: OfflineSyncItem[] = [];
  const carried = new Set<string>();

  for (const item of prev) {
    const fresh = nextById.get(item.localSessionId);
    if (fresh) {
      reconciled.push(fresh);
      carried.add(item.localSessionId);
    } else if (item.status === 'synced') {
      reconciled.push(item);
      carried.add(item.localSessionId);
    }
  }
  for (const item of next) {
    if (!carried.has(item.localSessionId)) reconciled.push(item);
  }
  return reconciled;
}

function removeLocalSession(
  prev: OfflineLocalSessionListItem[],
  localSessionId: string
): OfflineLocalSessionListItem[] {
  return prev.filter((item) => item.localSessionId !== localSessionId);
}

function appendUniqueSessionId(prev: string[], localSessionId: string): string[] {
  return prev.includes(localSessionId) ? prev : [...prev, localSessionId];
}

function removeNoteFailedSession(prev: string[], localSessionId: string): string[] {
  return prev.filter((id) => id !== localSessionId);
}

/**
 * Swaps a note-failure entry from its local id to the server id once sync
 * assigns one. Both note-failed lists are keyed by `localSessionId`, but the
 * only carrier of the local↔server mapping is the sync-queue row, which
 * `clearVisibleSyncedQueueItems` drops as soon as the backend session appears.
 * A later delete then arrives with the server id alone and would clear neither
 * list — leaving an undismissed warning for a session that no longer exists,
 * and a dismissed id persisted forever.
 *
 * Re-keying at `synced` (the one moment both ids are in hand) keeps the entry
 * addressable for the rest of its life. Both lists must be re-keyed together:
 * re-keying only the failure list would resurface a warning the user already
 * dismissed.
 */
function rekeyNoteFailedSession(
  prev: string[],
  localSessionId: string,
  serverSessionId: string
): string[] {
  if (!prev.includes(localSessionId)) return prev;
  return appendUniqueSessionId(
    removeNoteFailedSession(prev, localSessionId),
    serverSessionId
  );
}

function mergeRecoveredItems(
  prev: OfflineSyncItem[],
  recoveredSessionIds: string[]
): OfflineSyncItem[] {
  const existingIds = new Set(prev.map((item) => item.localSessionId));
  const newItems = recoveredSessionIds
    .filter((id) => !existingIds.has(id))
    .map(
      (id): OfflineSyncItem => ({
        localSessionId: id,
        userId: '',
        serverSessionId: null,
        localEncryptedPath: '',
        status: 'pending_sync',
        attemptCount: 0,
        lastAttemptAt: null,
        lastErrorCode: null,
        fileSizeBytes: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    );
  return newItems.length > 0 ? [...prev, ...newItems] : prev;
}

function findServerSessionIdForViewedLocalSession(
  items: Array<{ localSessionId: string; serverSessionId?: string | null }>,
  viewedLocalSessionId: string
): string | null {
  return (
    items.find((item) => item.localSessionId === viewedLocalSessionId)?.serverSessionId ??
    null
  );
}

function shouldRedirectViewedLocalSession(
  pathname: string | undefined,
  viewedLocalSessionId: string,
  serverSessionId: string | null
): serverSessionId is string {
  return Boolean(
    pathname?.startsWith('/scribe/session') &&
    viewedLocalSessionId &&
    serverSessionId &&
    serverSessionId !== viewedLocalSessionId
  );
}

function isViewingSyncedSession(
  pathname: string | undefined,
  viewedSessionId: string,
  localSessionId: string,
  serverSessionId: string
): boolean {
  return Boolean(
    pathname?.startsWith('/scribe/session') &&
    (viewedSessionId === localSessionId || viewedSessionId === serverSessionId)
  );
}

/**
 * Every query-key root whose cache can hold a rendered session-list row. Two
 * facts the keys don't show: `pages/scribe/sessions` returns null outright
 * when the new session list is off, so `GET_SESSIONS_LIST` is never written
 * on that path; and `GET_SESSIONS_LIST_FIRST_PAGE` is a sibling root, not a
 * longer key under it, so a prefix match never reaches it.
 *
 * Both consumers below read this one list: the sweep must not wake on a root
 * the probe cannot read, nor stay asleep on one it can.
 */
const SESSION_LIST_CACHE_ROOTS = [
  GET_SESSIONS_LIST,
  GET_SESSIONS_LIST_FIRST_PAGE,
  GET_SESSIONS,
  GET_INFINITE_SESSIONS,
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
] as const;

const SESSION_LIST_CACHE_ROOT_KEYS: ReadonlySet<unknown> = new Set(
  SESSION_LIST_CACHE_ROOTS
);

/** V1 caches `{ sessions }`; the infinite and v2.2 lists cache `{ pages: [{ sessions }] }`. */
type CachedSessionListData = {
  sessions?: Array<{ session_id?: string | null }>;
  pages?: Array<{ sessions?: Array<{ session_id?: string | null }> }>;
};

function cachedRowsContainSession(
  sessions: Array<{ session_id?: string | null }> | undefined,
  sessionId: string
): boolean {
  return Boolean(sessions?.some((session) => session.session_id === sessionId));
}

function hasSessionInSessionListCache(sessionId: string): boolean {
  return SESSION_LIST_CACHE_ROOTS.some((root) =>
    reactQueryClient
      .getQueriesData<CachedSessionListData>({ queryKey: [root] })
      .some(
        ([, data]) =>
          cachedRowsContainSession(data?.sessions, sessionId) ||
          Boolean(
            data?.pages?.some((page) =>
              cachedRowsContainSession(page.sessions, sessionId)
            )
          )
      )
  );
}

function hasSessionInDetailCache(sessionId: string): boolean {
  return Boolean(
    reactQueryClient.getQueryData<QuerySessionResponse>([
      GET_SESSION_BY_ID,
      { sessionId },
    ])
  );
}

/**
 * Raise a session's cached `duration` to `recordedDurationMs`, never lower it.
 *
 * The one place the "recorded duration only ever moves upward" invariant lives
 * *for the writers that carry a durable, accumulated value*. Three callers need
 * it and each reaches the cache from a different direction — a durable row
 * keyed by its server id, the same row keyed locally, and the per-take
 * `pending_sync` event — so stating the rule once is what stops one of them
 * drifting back to a plain assignment.
 *
 * **It is not the only writer of this field, by design.** The offline stop
 * freeze in `useRecordingButton` plain-assigns the live timer's elapsed seconds
 * onto the viewed session, and deliberately stays a plain assignment: it exists
 * to defeat a reseed race in the window before any durable value is available,
 * so it has to be able to set the field outright rather than negotiate with
 * whatever the cache happens to hold. Because it is sourced from a
 * second-floored live timer rather than from
 * `offline_sessions.recorded_duration_ms`, it can momentarily land a second
 * below the accumulated total on a multi-take consult. That is self-correcting:
 * the `pending_sync` merge and the queue refresh that follows it both run
 * through here and raise the field back. So read the invariant as "the durable
 * total is never dragged back", not as "this field is monotonic".
 *
 * **Why upward and not a write.** The sources measure different things and none
 * may drag another back. The server value covers what the backend has ingested;
 * the durable row covers what the device holds; the cached value can be a live
 * timer freeze that is ahead of the durable row for the window before the take
 * is queued. And `pending_sync` carries the finished TAKE's length rather than
 * the encounter total — Rust accumulates the total into
 * `offline_sessions.recorded_duration_ms` with
 * `COALESCE(recorded_duration_ms, 0) + ?` inside `Storage::queue_take` and never
 * puts it on that wire — so assigning there shortens a two-part consult to
 * whichever part stopped last.
 *
 * Updater-only, so an entry that is absent (or has since gone) is never minted
 * as a partial one. A null/zero/negative row value writes nothing at all rather
 * than 0: "never recorded" and "recorded nothing" have to stay
 * indistinguishable. `?? 0` floors an entry that carries no `duration` yet — the
 * offline stub omits the field rather than writing 0, and `Math.max` against
 * `undefined` is `NaN`.
 */
function mergeRecordedDurationUpward(
  sessionId: string,
  recordedDurationMs: number | null | undefined
): void {
  const recordedSeconds = recordedSecondsFromDurationMs(recordedDurationMs);
  if (recordedSeconds === undefined) return;
  reactQueryClient.setQueryData<QuerySessionResponse>(
    [GET_SESSION_BY_ID, { sessionId }],
    (old) =>
      old ? { ...old, duration: Math.max(old.duration ?? 0, recordedSeconds) } : old
  );
}

// Cold-boot rehydration (APP-7910): the offline-local RQ stub written on create
// is in-memory only, so after a quit + offline relaunch it's gone — the detail
// route's server query would then run, 404 offline, and render the full-page
// 404. For every pure local session in the durable Rust list (no
// `serverSessionId`) that has no cache entry, re-write the same offline-local
// stub the create path writes so `useQuerySessionById` stays disabled and the
// route renders the offline state instead. Sessions that already carry a server
// id are left to their real query and are never stubbed — only their recorded
// duration is merged in (see the branch below). An existing (possibly richer)
// cache entry is never clobbered — the recorded duration is merged onto it, and
// only ever upward.
//
// The stub is rebuilt *with* the settings the durable row carries. Identity
// alone is not enough: the mode/language/template/style pickers read this same
// cache entry, so a stub without them shows every picker on its default and the
// clinician's offline choice looks silently discarded. Display only — these are
// the values Rust already persisted, so nothing is written back here.
function rehydrateOfflineLocalSessionStubs(
  items: Array<{
    localSessionId: string;
    serverSessionId: string | null;
    dictationType?: string | null;
    language?: string | null;
    outputLanguage?: string | null;
    templateId?: string | null;
    noteStyle?: string | null;
    recordedDurationMs?: number | null;
    isConsent?: boolean | null;
  }>
): void {
  // A NULL template/note-style column means "no per-session pick — the sync
  // engine resolves the account default" (DSK-873), so the picker must show
  // that default rather than an empty selection. A non-NULL column is an
  // explicit pick and still wins, exactly as it does at sync.
  //
  // Read straight from the persisted snapshot rather than through
  // `resolveLastUsedRecorderSettings`: this runs on a cold boot, where the live
  // user object has not been fetched yet and the snapshot is the only populated
  // source anyway. It also keeps `useQueryUser` — and the whole app store
  // behind it — out of this module's import graph. Hoisted out of the loop
  // because it is invariant across the whole rehydration pass.
  const defaults = getCachedLastUsedRecorderSettings();
  // `?? defaults` would not be enough. `update_session_settings` uses
  // COALESCE, so a cleared setting round-trips as `''` rather than NULL — and
  // an empty string is not nullish, so it would pass through the fallback and
  // then be collapsed to `undefined` by `definedSetting` inside the stub
  // builder, landing on AUTO_MODE: exactly the mismatch this change exists to
  // remove. Treat blank as unset here, matching what `non_empty` does to the
  // same column at sync.
  const withDefault = (
    persisted: string | null | undefined,
    fallback: string | undefined
  ): string | undefined => (persisted?.trim() ? persisted : fallback);

  for (const item of items) {
    if (item.serverSessionId) {
      // A consult that already carries a server id is NOT stubbed. Its real
      // query owns that entry, and painting an offline-local stub over a
      // healthy server session hijacks its note pane — a fix that had to ship
      // once already. Only the recorded duration crosses.
      //
      // It has to cross, though. Once a server id exists the detail route shows
      // it, so the server id is the primary relaunch URL, and the only writer of
      // its `duration` was the same-process stop patch — which died with the
      // process. Without this, a reopened half-recorded consult resumes from
      // 00:00 while its audio is sitting in the queue.
      //
      // Upward-only and updater-only — see `mergeRecordedDurationUpward`. When
      // no entry exists yet (the ordinary cold boot, before the server query
      // resolves) this writes nothing; the timer seeds fall back to the same
      // queue row, which is what covers that window.
      mergeRecordedDurationUpward(item.serverSessionId, item.recordedDurationMs);
      continue;
    }
    // Un-poison the id: an online direct-mount to this not-yet-synced local id
    // on cold boot can 404 before this rehydration runs, permanently
    // adding it to notFoundSessionIds and short-circuiting the session query (and
    // downstream permission/transcript/document queries) even after the stub is
    // back. Clearing it here lets those queries self-heal once the stub returns.
    notFoundSessionIds.remove(item.localSessionId);
    if (hasSessionInDetailCache(item.localSessionId)) {
      // The entry survives untouched except for its recorded duration, which is
      // the one field an existing entry can hold a staler value of than the
      // durable row holds — its only other writers are the timer freeze at stop
      // and the per-take `pending_sync` event, neither of which knows the
      // encounter total. `offline_sessions.recorded_duration_ms` is the sum
      // `queue_take` maintains across takes, so this is the path that carries a
      // two-part consult's real length onto a session with no server id yet.
      // Upward-only and updater-only — see `mergeRecordedDurationUpward`.
      //
      // Consent is deliberately NOT merged the same way, and the asymmetry is
      // the point. The durable consent write is fire-and-forget and swallows
      // its own failure, so a column still reading never-asked while the entry
      // holds an answer the clinician gave seconds ago is reachable. Duration
      // has an ordering rule that makes a merge safe (the durable sum is never
      // behind); consent has none, so merging it would let a stale NULL stomp a
      // fresher answer and re-prompt — the symptom this hydration exists to
      // remove. An existing entry is already the freshest thing there is here.
      mergeRecordedDurationUpward(item.localSessionId, item.recordedDurationMs);
      continue;
    }
    reactQueryClient.setQueryData(
      [GET_SESSION_BY_ID, { sessionId: item.localSessionId }],
      buildOfflineLocalSessionStub(item.localSessionId, {
        transcribeMode: item.dictationType,
        language: item.language,
        outputLanguage: item.outputLanguage,
        templateId: withDefault(item.templateId, defaults?.templateId),
        noteStyle: withDefault(item.noteStyle, defaults?.voiceStyle),
        // The audio the consult already holds, summed across its recordings by
        // Rust. Without it the rebuilt stub says "never recorded": the header
        // reads 00:00 and a resume starts its timer from zero, because the only
        // other source of elapsed time is the Rematch timer and that died with
        // the process. Restoring it here is what makes the reopened consult
        // present as stopped-with-audio, which is what it is.
        recordedDurationMs: item.recordedDurationMs,
        // The patient-consent answer, and the same argument in a different
        // register: the gate resolves from this cache entry, so a stub rebuilt
        // without it re-prompts a clinician who answered while offline — on an
        // answer the device has held on disk the whole time. Carried verbatim,
        // `false` included, so a refusal stays distinguishable from a consult
        // nobody was asked about.
        isConsent: item.isConsent,
      })
    );
  }
}

function canClearSyncedQueueItem(
  item: OfflineSyncItem,
  pathname: string | undefined,
  viewedSessionId: string
): boolean {
  if (item.status !== 'synced' || !item.serverSessionId) return false;

  return (
    isViewingSyncedSession(
      pathname,
      viewedSessionId,
      item.localSessionId,
      item.serverSessionId
    ) ||
    hasSessionInDetailCache(item.serverSessionId) ||
    hasSessionInSessionListCache(item.serverSessionId)
  );
}

function removeVisibleSyncedQueueItems(
  prev: OfflineSyncItem[],
  pathname: string | undefined,
  viewedSessionId: string
): OfflineSyncItem[] {
  const next = prev.filter(
    (item) => !canClearSyncedQueueItem(item, pathname, viewedSessionId)
  );
  return next.length === prev.length ? prev : next;
}

/**
 * How long the "Synced" tick stays up after a recording finishes syncing,
 * before it dismisses itself.
 *
 * The tick is an acknowledgement, not a status: the work is done and there is
 * nothing left for the clinician to act on, so it should read as the end of the
 * `Syncing… → Synced` sequence rather than as a permanent badge on the row.
 * Long enough to be noticed if they were watching, short enough that it is gone
 * by the time they look back.
 *
 * Exported so the tests advance fake timers by this exact duration rather than
 * keeping a copy of it in step by hand.
 */
export const SYNCED_BADGE_DISMISS_MS = 5_000;

/**
 * Add a recording's ids to the dismissed-tick set.
 *
 * Both ids, because a list row is keyed locally until its server session exists
 * and by the server id afterwards; dismissing only one would let the tick
 * reappear the moment the real session arrived.
 *
 * Returns `prev` by identity when everything was already there, so a repeated
 * dismissal does not re-render every list surface.
 */
function addDismissedSyncedBadgeIds(
  prev: string[],
  ids: (string | null | undefined)[]
): string[] {
  const additions = ids.filter(
    (id): id is string => typeof id === 'string' && id.length > 0 && !prev.includes(id)
  );
  return additions.length === 0 ? prev : [...prev, ...additions];
}

function shouldSweepSyncedQueueForQueryKey(queryKey: readonly unknown[]): boolean {
  return (
    SESSION_LIST_CACHE_ROOT_KEYS.has(queryKey[0]) || queryKey[0] === GET_SESSION_BY_ID
  );
}

function findQueuedItem(
  items: OfflineSyncItem[],
  localSessionId: string
): OfflineSyncItem | undefined {
  return items.find((item) => item.localSessionId === localSessionId);
}

function syncFailureWillRetry(status: OfflineSyncItem['status']): boolean | null {
  if (status === 'auth_required' || status === 'unrecoverable') return false;
  if (status === 'sync_failed' || status === 'upload_failed') return true;
  return null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOfflineRecordingEvents(): void {
  const router = useRouter();
  const currentSessionId = useCurrentSessionId();
  const buildSessionPath = useBuildSessionPath();
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setActiveRecordingId = useSetAtom(offlineActiveRecordingIdAtom);
  const setCapturePaused = useSetAtom(offlineCapturePausedAtom);
  const setConnectivity = useSetAtom(offlineConnectivityStateAtom);
  const setOfflineLocalSessions = useSetAtom(offlineLocalSessionsAtom);
  const setQueue = useSetAtom(offlineSyncQueueAtom);
  const setNoteFailedDismissedSessionIds = useSetAtom(
    offlineNoteFailedDismissedSessionIdsAtom
  );
  const setNoteFailedSessionIds = useSetAtom(offlineNoteFailedSessionIdsAtom);
  const setPendingServerDeleteIds = useSetAtom(offlinePendingServerDeleteIdsAtom);
  const setOfflinePatientLinksBySessionId = useSetAtom(
    offlinePatientLinksBySessionIdAtom
  );
  const offlineDeletedLocalSessionIds = useAtomValue(offlineDeletedLocalSessionIdsAtom);
  const setOfflineDeletedLocalSessionIds = useSetAtom(offlineDeletedLocalSessionIdsAtom);
  const setDismissedSyncedBadgeIds = useSetAtom(offlineDismissedSyncedBadgeIdsAtom);
  const jotaiStore = useStore();
  const deletedLocalSessionIdsRef = useRef(new Set(offlineDeletedLocalSessionIds));
  const queue = useAtomValue(offlineSyncQueueAtom);

  // Ref keeps the toast's formatMessage call current across locale switches
  // without re-registering listeners (empty-deps effect).
  const intl = useIntl();
  const intlRef = useRef(intl);
  useEffect(() => {
    intlRef.current = intl;
  }, [intl]);
  useEffect(() => {
    deletedLocalSessionIdsRef.current = new Set(offlineDeletedLocalSessionIds);
  }, [offlineDeletedLocalSessionIds]);

  // The pending-delete hydration, published out of the listener effect so the
  // rehydration effect below can re-run it without duplicating the read.
  const refreshPendingServerDeletesRef = useRef<(reason: string) => void>(() => {});
  // Bumped by `rehydrateUserScopedAtoms` when the cached user scope resolves or
  // changes — the moment the user-scoped mirror is re-read from a DIFFERENT
  // bucket. The durable read has to run again then: the value the initial
  // hydration wrote belonged to the previous scope (or to no scope at all, in
  // which case the scoped adapter dropped the write on the floor), so without
  // this a clinician who signs in after the app was already running has an
  // empty mirror until something else happens to refresh it.
  const userScopeRehydrationSignal = useAtomValue(userScopedStorageRehydratedAtom);
  const appliedRehydrationSignalRef = useRef(userScopeRehydrationSignal);

  const routerRef = useRef(router);
  const currentSessionIdRef = useRef(currentSessionId);
  const buildSessionPathRef = useRef(buildSessionPath);
  const queueRef = useRef(queue);
  useEffect(() => {
    routerRef.current = router;
    currentSessionIdRef.current = currentSessionId;
    buildSessionPathRef.current = buildSessionPath;
    queueRef.current = queue;
  }, [router, currentSessionId, buildSessionPath, queue]);

  useEffect(() => {
    if (!isTauri()) return;

    const unlistenFns: UnlistenFn[] = [];
    const hydrationRetryTimers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    // Local sessions this renderer has already seen reach `synced` (DSK-935).
    //
    // Rust deletes a recording's durable rows in `finalize_synced` the instant
    // its upload completes, so a `list()` snapshot read *before* that deletion
    // still names it at an in-flight status. A batch reconnect produces exactly
    // that: every queued recording's `creating_session` fires a queue refresh,
    // each response replaces the whole queue, and one of them was read while a
    // sibling was still uploading. The stale response then resurrects a
    // recording that has already finished, pinned at `uploading_audio`.
    //
    // Nothing can clear it again. Rust will never emit another event for a
    // recording it has deleted, and `clearVisibleSyncedQueueItems` only removes
    // rows whose status is exactly `synced` — so the row spins "Syncing…" for
    // the rest of the app's life while the session itself is perfectly synced.
    //
    // `synced` is terminal and a local session id is never reused, so an id in
    // here is safe to refuse forever. Bounded by the recordings synced in this
    // app run, and dropped with the renderer.
    //
    // Deliberately NOT evicted, and not bounded by a cap. The stale-snapshot
    // window it guards has no end the renderer can observe: Rust deletes the
    // durable rows at `finalize_synced` and never emits for that recording
    // again, so any snapshot still naming it is stale forever, and an id
    // dropped from this ledger is an id that can be resurrected — the exact
    // DSK-935 bug. The cost is one ~36-char id per recording synced in this app
    // run (a heavy day of 500 is well under 50 KB), against a Set that dies
    // with the webview.
    const syncedLocalSessionIds = new Set<string>();

    // Monotonic refresh generation. Several `list()` calls can be in flight at
    // once on a batch reconnect and nothing orders their replies, so a reply is
    // only allowed to write the queue when it is newer than the last one that
    // actually did. `latestQueueRefresh` stamps each call as it STARTS;
    // `appliedQueueRefresh` only advances when a reply is applied, so a rejected
    // newest refresh cannot suppress a good older one.
    let latestQueueRefresh = 0;
    let appliedQueueRefresh = 0;

    // Pending "the tick has had its moment" timers, keyed by local session id.
    //
    // Tracked rather than fired-and-forgotten so a recording deleted while its
    // tick is still up cancels its own timer, and so unmount can clear the lot
    // instead of leaving callbacks that would set an atom on a dead renderer.
    // Bounded by the recordings synced in the last few seconds, and each entry
    // deletes itself as it fires.
    const syncedDismissalTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const cancelSyncedDismissal = (localSessionId: string) => {
      const timer = syncedDismissalTimers.get(localSessionId);
      if (timer === undefined) return;
      clearTimeout(timer);
      syncedDismissalTimers.delete(localSessionId);
    };

    /**
     * Stop showing this recording's "Synced" tick after
     * {@link SYNCED_BADGE_DISMISS_MS}.
     *
     * Surface-independent by construction, which is the whole point: it names no
     * query key and no list. Every surface that shows the tick — both session
     * lists, the sidebar nav and the session header — resolves it through the
     * dismissed-ids atom, so one write clears all of them at once. This cannot
     * repeat the bug where a clear knew only one surface's cache key and left
     * the tick up on the others.
     *
     * **It dismisses the badge and deliberately does NOT remove the queue row.**
     * That row is what synthesises a session-list entry while the real backend
     * session is still absent, so taking it out to hide a tick would take the
     * session off the list with it, for the whole invalidate/refetch gap — a
     * much worse outcome than a tick that overstays. Removing the row stays the
     * job of the two existing sweeps, which only fire once the backend session
     * is genuinely visible somewhere.
     *
     * Leaving the row is also what keeps this clear of the queue's own
     * lifecycle. `reconcileQueueWithDurableList` still carries the `synced` row
     * across a hydration, and `syncedLocalSessionIds` still filters the stale
     * snapshots that would otherwise resurrect it at an in-flight status — both
     * exactly as before.
     */
    const scheduleSyncedDismissal = (
      localSessionId: string,
      serverSessionId: string | null | undefined
    ) => {
      cancelSyncedDismissal(localSessionId);
      syncedDismissalTimers.set(
        localSessionId,
        setTimeout(() => {
          syncedDismissalTimers.delete(localSessionId);
          if (cancelled) return;
          setDismissedSyncedBadgeIds((prev) =>
            addDismissedSyncedBadgeIds(prev, [localSessionId, serverSessionId])
          );
        }, SYNCED_BADGE_DISMISS_MS)
      );
    };

    // -----------------------------------------------------------------------
    // Named handlers — each closes over the atom setters from hook scope.
    // Extracted so setup()'s cognitive complexity stays manageable.
    // -----------------------------------------------------------------------

    // Whether this webview is the one that speaks for the app. Used by the
    // handlers below whose effect is app-wide (a toast, a lifecycle event)
    // rather than per-window — see the toast gates further down for the full
    // rationale.
    const isMainWindow = () => getCurrentWindow().label === MAIN_WINDOW_LABEL;

    const isDeletedLocalSession = (localSessionId: string) =>
      deletedLocalSessionIdsRef.current.has(localSessionId) ||
      jotaiStore.get(offlineDeletedLocalSessionIdsAtom).includes(localSessionId);

    const dropDeletedLocalSession = (localSessionId: string) => {
      setQueue((prev) => removeQueuedSession(prev, localSessionId));
      setOfflineLocalSessions((prev) => removeLocalSession(prev, localSessionId));
      setNoteFailedSessionIds((prev) => removeNoteFailedSession(prev, localSessionId));
      setNoteFailedDismissedSessionIds((prev) =>
        removeNoteFailedSession(prev, localSessionId)
      );
    };

    const onCaptureStarted = (e: Event<CaptureStartedPayload>) => {
      if (isDeletedLocalSession(e.payload.localSessionId)) {
        dropDeletedLocalSession(e.payload.localSessionId);
        return;
      }
      setActiveRecordingId(e.payload.localSessionId);
      // Datadog, for the same reason `recording_count` goes there: a new
      // property on the Snowplow `offline_recording_started` event needs an Iglu
      // ADDITION published to prod before the emitting frontend merges, and
      // shipping it the other way round fails validation for the whole event.
      // The pipeline event carries free-form attributes and no schema, so the
      // resume rate is queryable from day one of the rollout — which is the
      // window it is needed in.
      logSessionPipelineDatadogEvent('offline_session_capture_started', {
        session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
        session_id_local: e.payload.localSessionId,
        take_index: e.payload.takeIndex,
        is_resume:
          e.payload.takeIndex === undefined ? undefined : e.payload.takeIndex > 0,
      });
      trackOfflineRecordingStarted({
        local_session_id: e.payload.localSessionId,
        platform: 'desktop',
        capture_region: e.payload.captureRegion,
        microphone_source: e.payload.microphoneSource,
        source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
      });
    };

    // offline_recording:capture_ended — the capture left Rust's active slot, so
    // nothing is recording any more. Rust emits this before any of stop()'s
    // fallible work, which makes it the authority on the transition: a stop that
    // fails partway through (encrypt, storage) still resets the UI here, and a
    // stop that fails *before* the capture was taken emits nothing, leaving the
    // still-running capture controllable instead of stranded behind an idle UI.
    // Distinct from pending_sync, which only fires once a recording is durably
    // queued — a failed stop never gets that far.
    const onCaptureEnded = (e: Event<CaptureEndedPayload>) => {
      const { localSessionId } = e.payload;
      if (isDeletedLocalSession(localSessionId)) {
        dropDeletedLocalSession(localSessionId);
        return;
      }
      // Only the live capture's own end resets the shared atoms — a late event
      // for a superseded session must not clear a capture that has since started.
      if (jotaiStore.get(offlineActiveRecordingIdAtom) !== localSessionId) return;
      setActiveRecordingId(null);
      setCapturePaused(false);
      // Here rather than in `useOfflineRecorder.stop()` because this event is
      // the authority on the transition (see above), so a capture Rust ends on
      // its own still hands the microphone back. Main-window-gated with the
      // toasts below: the lifecycle event is app-wide.
      if (isMainWindow()) restoreIdleStreamAfterCapture(localSessionId);
    };

    const hydrateFromList = (
      items: Awaited<ReturnType<typeof offlineRecordingBridge.list>>
    ) => {
      const visibleItems = items.filter(
        (item) =>
          !isDeletedLocalSession(item.localSessionId) &&
          // A snapshot naming an already-synced recording is stale by
          // construction — Rust dropped its rows at `finalize_synced`.
          !syncedLocalSessionIds.has(item.localSessionId)
      );
      setQueue((prev) =>
        reconcileQueueWithDurableList(prev, visibleItems.map(toSyncItem))
      );
      // Re-seed the offline-local detail-cache stubs lost on quit so a session
      // interacted with offline stays reachable after relaunch (APP-7910).
      rehydrateOfflineLocalSessionStubs(visibleItems);
      setOfflinePatientLinksBySessionId((prev) =>
        mergeOfflinePatientLinks(prev, visibleItems)
      );
      const viewedLocalSessionId = currentSessionIdRef.current;
      const serverSessionId = findServerSessionIdForViewedLocalSession(
        visibleItems,
        viewedLocalSessionId
      );
      if (
        shouldRedirectViewedLocalSession(
          routerRef.current.pathname,
          viewedLocalSessionId,
          serverSessionId
        )
      ) {
        void routerRef.current
          .replace(buildSessionPathRef.current(serverSessionId))
          .catch((err: unknown) => {
            logger.warn(
              '[useOfflineRecordingEvents] re-route to server session failed',
              err
            );
          });
      }
    };

    // Returns the read so a caller can wait for the queue to reflect it — the
    // Retry in the note body needs that to avoid clearing its pending state
    // before the row it just drained has been re-read. The catch below is the
    // single place a failed read is reported.
    const refreshQueueFromBridge = (reason: string, attemptsLeft = 0): Promise<void> => {
      const generation = (latestQueueRefresh += 1);
      return offlineRecordingBridge
        .list()
        .then((items) => {
          if (cancelled) return;
          // Drop a reply older than one already APPLIED — applying it would
          // rewind every row the newer snapshot disagreed about.
          //
          // Deliberately compared against the applied watermark rather than
          // against `latestQueueRefresh` (the newest snapshot merely *started*):
          // when the newest `list()` rejects, gating on the started generation
          // would throw away this good older reply too and leave the queue stale
          // until some later event happened to self-heal it.
          if (generation <= appliedQueueRefresh) return;
          appliedQueueRefresh = generation;
          hydrateFromList(items);
          acknowledgeListedHandovers(items.map((item) => item.localSessionId));
        })
        .catch((err: unknown) => {
          logger.warn(`[useOfflineRecordingEvents] ${reason} queue refresh failed`, err);
          // Only the note Retry awaits this read and can tell the user it
          // failed. Every other caller fires and forgets, and a later event
          // refresh self-heals it, so their promise must stay non-throwing.
          if (reason === NOTE_UNAVAILABLE_RETRY_REFRESH_REASON) throw err;
          if (cancelled || attemptsLeft <= 0) return;
          const delayMs =
            HYDRATION_RETRY_DELAYS_MS[HYDRATION_RETRY_DELAYS_MS.length - attemptsLeft] ??
            0;
          const timer = setTimeout(() => {
            void refreshQueueFromBridge(reason, attemptsLeft - 1);
          }, delayMs);
          hydrationRetryTimers.push(timer);
        });
    };

    // Reconcile the render-time mirror of queued server deletes against the
    // durable table.
    //
    // The mirror is what hides a deleted session from the very first painted
    // frame after a relaunch — it is read synchronously out of localStorage
    // during render, which this asynchronous read can never be sequenced
    // against. So this call is NOT what makes the first frame correct; it is
    // what keeps the cache honest afterwards, in Rust's favour: an id the
    // backend no longer owes a delete for stops hiding its session, with no
    // status rule duplicated on this side.
    //
    // The snapshot is taken BEFORE the read starts, so an enqueue that lands
    // while it is in flight — this window or another one — is not erased by a
    // reply that could not have seen it. Retried on rejection for the same
    // reason the queue hydration is: the command resolves its user from the
    // cached identity and refuses without one, and nothing else re-triggers it.
    const refreshPendingServerDeletes = (
      reason: string,
      attemptsLeft = 0
    ): Promise<void> => {
      const snapshot = jotaiStore.get(offlinePendingServerDeleteIdsAtom);
      // The clinician this read is being taken FOR. A queued delete is private
      // to whoever made it, and this reply cannot know the scope moved while it
      // was in flight — `cancelled` only covers unmount, which a sign-in is
      // not. Applying one clinician's owed ids to the next one's mirror would
      // also PERSIST them under that clinician's storage key, where nothing on
      // the honest path clears them again.
      const scopeAtRead = getUserStorageScope();
      return offlineRecordingBridge
        .listServerDeletes()
        .then((owed) => {
          if (cancelled) return;
          if (getUserStorageScope() !== scopeAtRead) return;
          setPendingServerDeleteIds((prev) =>
            reconcilePendingServerDeleteIds(prev, snapshot, owed)
          );
        })
        .catch((err: unknown) => {
          logger.warn(
            `[useOfflineRecordingEvents] ${reason} queued-delete refresh failed`,
            err
          );
          if (cancelled || attemptsLeft <= 0) return;
          const delayMs =
            HYDRATION_RETRY_DELAYS_MS[HYDRATION_RETRY_DELAYS_MS.length - attemptsLeft] ??
            0;
          // Released once it fires. The array is the unmount sweep, and a scope
          // change can start a fresh retry chain every time a clinician signs
          // in — keeping spent handles would grow it for the life of the
          // process with nothing left to cancel.
          const timer = setTimeout(() => {
            const spent = hydrationRetryTimers.indexOf(timer);
            if (spent !== -1) hydrationRetryTimers.splice(spent, 1);
            void refreshPendingServerDeletes(reason, attemptsLeft - 1);
          }, delayMs);
          hydrationRetryTimers.push(timer);
        });
    };
    refreshPendingServerDeletesRef.current = (reason: string) => {
      void refreshPendingServerDeletes(reason, HYDRATION_RETRY_DELAYS_MS.length);
    };

    // Publish the coordinated read so surfaces that need the queue re-read ask
    // for it here instead of writing the atom themselves — the reconcile, the
    // synced-ledger refusal and the ordering watermark above are exactly what a
    // second writer would skip.
    const unregisterQueueRefresh =
      registerOfflineSyncQueueRefresh(refreshQueueFromBridge);

    const clearVisibleSyncedQueueItems = () => {
      setQueue((prev) =>
        removeVisibleSyncedQueueItems(
          prev,
          routerRef.current.pathname,
          currentSessionIdRef.current
        )
      );
    };

    // Both session-list roots. Used by two unrelated cleanups — a recording
    // that finished syncing, and a queued delete the backend has accepted —
    // because both need the list re-fetched before the renderer stops
    // compensating for it locally.
    const invalidateSessionListCaches = () =>
      Promise.all([
        invalidateSessionsQuery(),
        reactQueryClient.invalidateQueries({ queryKey: [GET_SESSIONS_LIST] }),
      ]);

    const unsubscribeQueryCache = reactQueryClient.getQueryCache().subscribe((event) => {
      const queryKey: unknown = event.query.queryKey;
      if (Array.isArray(queryKey) && shouldSweepSyncedQueueForQueryKey(queryKey)) {
        clearVisibleSyncedQueueItems();
      }
    });

    // offline_recording:pending_sync — WAV finalised, queued for sync.
    const onPendingSync = (e: Event<PendingSyncPayload>) => {
      const { localSessionId, recordedDurationMs, fileSizeBytes, captureRegion } =
        e.payload;
      if (isDeletedLocalSession(localSessionId)) {
        dropDeletedLocalSession(localSessionId);
        return;
      }
      setActiveSessionId(null);
      setActiveRecordingId(null);
      trackOfflineRecordingQueued({
        local_session_id: localSessionId,
        recorded_duration_ms: recordedDurationMs,
        file_size_bytes: fileSizeBytes,
        capture_region: captureRegion,
        source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
      });
      // Symmetric with hook.stop(): clear the capture-scoped paused flag so a
      // capture finalised via pending_sync (e.g. a Rust-side auto-finalize of a
      // paused capture, without hook.stop() running) can't leave the atom true.
      setCapturePaused(false);
      // Surface the recorded duration onto the local session so the header
      // flips to the stopped/recorded state (duration + "Resume") instead of
      // reverting to the idle 00:00/"Transcribe" empty state after stop
      // (APP-7249). An offline session is a client-only stub with no server
      // write, so the session object never otherwise learns it recorded — this
      // is the offline analogue of the server-set `duration` an online stop
      // produces, and it's what `useUpdateTimerFromSession` seeds the shared
      // recording timer from. `session.duration` is in seconds.
      //
      // Upward-only, because `recordedDurationMs` here is the length of the take
      // that just finished and not the encounter total — the argument in full is
      // on `mergeRecordedDurationUpward`. This stays the fast flip to the
      // recorded state (it is the only write on the Rust-side auto-finalize
      // path, where no stop freeze ran) and the accumulated total lands a moment
      // later on the queue refresh below, which merges it the same way.
      mergeRecordedDurationUpward(localSessionId, recordedDurationMs);
      // Refresh the full queue from Rust so the new row is reflected exactly.
      void refreshQueueFromBridge('pending_sync');
    };

    const onConnectivityChanged = (e: Event<ConnectivityChangedPayload>) => {
      setConnectivity(e.payload.state);
    };

    // offline_recording:status_changed — the orchestrator's single source of
    // truth for a recording's sync-lifecycle status. Replaces the old direct
    // session_engine:session_created / upload_complete / task_failed
    // subscriptions: the orchestrator already maps engine steps + retry intent
    // to the durable status and only emits for in-flight offline recordings, so
    // the FE no longer needs the shared-channel `inQueue` cross-talk guards.
    // patchQueueItem is a no-op when the row isn't present, so none is needed.
    // Consuming this event also surfaces the intermediate `uploading_audio`
    // status, which the session_engine:* path never reflected.
    const onStatusChanged = (e: Event<OfflineStatusChangedPayload>) => {
      const { localSessionId, status, attemptCount, captureRegion, lastErrorCode } =
        e.payload;
      if (isDeletedLocalSession(localSessionId)) {
        dropDeletedLocalSession(localSessionId);
        return;
      }
      const queuedItem = findQueuedItem(queueRef.current, localSessionId);
      setQueue((prev) =>
        patchQueueItem(prev, localSessionId, {
          status,
          ...(attemptCount != null ? { attemptCount } : {}),
          ...(captureRegion ? { captureRegion } : {}),
          // Only overwrite on a failure transition (success transitions omit the
          // field on the wire); keep any prior code otherwise.
          ...(lastErrorCode != null ? { lastErrorCode } : {}),
        })
      );
      // Once the server session exists the recording can appear in the session
      // list — refresh it (mirrors the old session_engine:session_created path).
      if (status === 'creating_session') {
        invalidateSessionsQuery().catch((err: unknown) => {
          logger.warn(
            '[useOfflineRecordingEvents] status_changed sessions invalidation failed',
            err
          );
        });
        // Rust persists `server_session_id` to the durable row *before* emitting
        // this transition, but the event payload carries only the local id — so
        // the in-memory queue item still has `serverSessionId: null` until we
        // re-read the durable list. Refresh it now so the item is keyed by its
        // server id. Without this the session detail header (which resolves its
        // status by matching the route's server id against the queue) and the
        // session list's status patch both miss the row once it leaves the
        // pre-server-id window.
        //
        // One refresh, not two: this used to fire an identical
        // `refreshQueueFromBridge('creating_session')` above as well, which put
        // two unordered `list()` replies in flight per recording for no extra
        // information — doubling the stale-snapshot surface this handler is the
        // main source of (DSK-935).
        void refreshQueueFromBridge('creating_session');
      }
      if (status === 'uploading_audio') {
        logSessionPipelineDatadogEvent('offline_chunk_upload_started', {
          session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
          session_id: queuedItem?.serverSessionId ?? undefined,
          session_id_local: localSessionId,
          attempt_count: attemptCount ?? queuedItem?.attemptCount ?? undefined,
        });
      }
      if (isOfflineFailedStatus(status)) {
        logSessionPipelineDatadogEvent(
          status === 'upload_failed'
            ? 'offline_chunk_upload_failure'
            : 'offline_session_sync_failure',
          {
            session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
            session_id: queuedItem?.serverSessionId ?? undefined,
            session_id_local: localSessionId,
            phase: status,
            error_type: lastErrorCode ?? queuedItem?.lastErrorCode ?? undefined,
            attempt_count: attemptCount ?? queuedItem?.attemptCount ?? undefined,
          },
          'warn'
        );
        trackOfflineSyncFailed({
          local_session_id: localSessionId,
          session_id: queuedItem?.serverSessionId ?? null,
          phase: status,
          error_code: lastErrorCode ?? queuedItem?.lastErrorCode ?? null,
          attempt_count: attemptCount ?? queuedItem?.attemptCount ?? null,
          will_retry: syncFailureWillRetry(status),
          capture_region: captureRegion ?? queuedItem?.captureRegion ?? 'unknown',
          source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
        });
      }
      // auth_required is the only status that requires user action — show a
      // "sign in again" toast. Pin to authRequired copy if lastErrorCode maps
      // to a passive descriptor (null/unknown code → generic, which contradicts
      // the needs-action intent).
      if (status === 'auth_required') {
        const resolved = getOfflineRecordingErrorMessage(lastErrorCode);
        const message =
          resolved.severity === 'needs_action'
            ? resolved
            : offlineRecordingErrorMessages.authRequired;
        toast({
          tone: 'warning',
          title: intlRef.current.formatMessage(message.title),
          description: intlRef.current.formatMessage(message.description),
          // Give the user a way to act on the "sign in again" prompt: the
          // offline sync stalled on an expired/missing session, so relaunch the
          // desktop Auth0 flow. Only auth_required is actionable — the passive
          // sync/upload states keep retrying on their own.
          action: createElement(
            ToastActionButton,
            {
              tone: 'warning',
              onClick: () => {
                void launchDesktopAuthLogin().catch((err: unknown) => {
                  logger.warn(
                    '[useOfflineRecordingEvents] sign-in from auth_required toast failed',
                    err
                  );
                });
              },
            },
            intlRef.current.formatMessage(signInActionMessages.signIn)
          ),
        });
      }
    };

    // offline_recording:note_failed — note generation failed (non-fatal: the
    // session + audio synced fine). The orchestrator already scoped this to an
    // offline recording, so no cross-talk guard is needed (unlike the shared
    // session_engine:note_failed channel this replaces).
    const onNoteFailed = (e: Event<OfflineNoteFailedPayload>) => {
      const { localSessionId } = e.payload;
      if (isDeletedLocalSession(localSessionId)) return;
      const queuedItem = findQueuedItem(queueRef.current, localSessionId);
      logSessionPipelineDatadogEvent(
        'offline_note_generation_failed',
        {
          session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
          session_id: queuedItem?.serverSessionId ?? undefined,
          session_id_local: localSessionId,
          error_type: e.payload.errorCode ?? undefined,
        },
        'warn'
      );
      trackOfflineNoteGenerationFailed({
        local_session_id: localSessionId,
        session_id: queuedItem?.serverSessionId ?? null,
        generation_trigger: 'offline_sync_auto_trigger',
        error_code: e.payload.errorCode ?? null,
        will_retry: false,
        source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
      });
      logger.warn({
        msg: '[useOfflineRecordingEvents] offline note generation failed',
        localSessionId,
        errorCode: e.payload.errorCode ?? null,
      });
      setNoteFailedSessionIds((prev) => appendUniqueSessionId(prev, localSessionId));
    };

    // offline_recording:synced — the orchestrator finished the recording (audio
    // safely on Heidi; .enc + tracking row already cleaned in Rust). Keep a
    // temporary synced queue row until the backend session appears in the list, so
    // the synthetic row does not vanish during the invalidate/refetch gap.
    const onSynced = (e: Event<OfflineSyncedPayload>) => {
      const {
        localSessionId,
        sessionId,
        attemptCount,
        recordedDurationMs,
        fileSizeBytes,
        captureRegion,
        recordingCount,
        hadLocalRecordingTime,
      } = e.payload;
      // A server session whose post-drop audio this consult held stops
      // routing note settings to it: the row is gone from here on.
      forgetOfflineHandover(localSessionId);
      if (isDeletedLocalSession(localSessionId)) {
        dropDeletedLocalSession(localSessionId);
        return;
      }
      // Record the termination before anything async: from here on, a durable
      // snapshot naming this recording is stale by construction. See
      // `syncedLocalSessionIds`.
      syncedLocalSessionIds.add(localSessionId);
      const queuedItem = findQueuedItem(queueRef.current, localSessionId);
      logSessionPipelineDatadogEvent('offline_session_sync_completed', {
        session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
        session_id: sessionId ?? queuedItem?.serverSessionId ?? undefined,
        session_id_local: localSessionId,
        attempt_count: attemptCount ?? queuedItem?.attemptCount ?? undefined,
        // How many recordings this consult synced as one note — the only
        // visibility anyone has into whether clinicians actually resume an
        // offline consult in the field, since the queue presents a multi-part
        // consult as a single row.
        //
        // Datadog rather than Snowplow deliberately. A new property on the
        // `offline_sync_completed` Snowplow event needs an Iglu ADDITION bump
        // published to prod and Snowtype regenerated BEFORE the frontend
        // emitting it merges; ship it the other way round and every
        // `offline_sync_completed` event fails Iglu validation and lands in the
        // bad-events stream, taking the existing funnel down with it. Datadog
        // pipeline events carry a free-form attribute bag and no schema, so this
        // number is queryable the day the rollout starts.
        recording_count: recordingCount ?? queuedItem?.recordingCount ?? undefined,
        // Whether this consult kept the instant it was recorded or fell back to
        // the backend's sync-time stamp — the only way to confirm that fix in
        // the field, and the only way to size the pre-upgrade cohort it
        // knowingly skips (a consult already sitting unsynced when the build
        // lands has no stamp and reports `false` until retention drains it).
        //
        // Datadog and not Snowplow, for the same reason as `recording_count`
        // directly above: a new property on the `offline_sync_completed`
        // Snowplow event needs an Iglu ADDITION bump live in prod before the
        // frontend emitting it merges, or every event in that funnel fails
        // validation. This is queryable on day one and takes nothing down.
        had_local_recording_time: hadLocalRecordingTime ?? undefined,
      });
      trackOfflineSyncCompleted({
        local_session_id: localSessionId,
        session_id: sessionId ?? queuedItem?.serverSessionId ?? null,
        attempt_count: attemptCount ?? queuedItem?.attemptCount ?? null,
        recorded_duration_ms:
          recordedDurationMs ?? queuedItem?.recordedDurationMs ?? null,
        file_size_bytes: fileSizeBytes ?? queuedItem?.fileSizeBytes ?? null,
        capture_region: captureRegion ?? queuedItem?.captureRegion ?? 'unknown',
        source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
      });
      setOfflineLocalSessions((prev) => removeLocalSession(prev, localSessionId));
      // Re-key any note-failure state onto the server id while both ids are
      // still in hand — see `rekeyNoteFailedSession`.
      if (sessionId) {
        setNoteFailedSessionIds((prev) =>
          rekeyNoteFailedSession(prev, localSessionId, sessionId)
        );
        setNoteFailedDismissedSessionIds((prev) =>
          rekeyNoteFailedSession(prev, localSessionId, sessionId)
        );
      }
      setQueue((prev) =>
        patchQueueItem(prev, localSessionId, {
          status: 'synced',
          ...(sessionId ? { serverSessionId: sessionId } : {}),
        })
      );
      // End of the `Syncing… → Synced` sequence. Without this the tick only ever
      // left when the backend session happened to appear in a watched cache, so
      // a clinician on a route with no session list mounted kept it all run.
      scheduleSyncedDismissal(localSessionId, sessionId ?? queuedItem?.serverSessionId);
      void invalidateSessionListCaches()
        .then(() => {
          clearVisibleSyncedQueueItems();
        })
        .catch((err: unknown) => {
          logger.warn(
            '[useOfflineRecordingEvents] synced sessions invalidation failed',
            err
          );
        });
      if (sessionId) {
        invalidateSessionById(sessionId);
        void invalidateTranscriptBySessionId(sessionId).catch((err: unknown) => {
          logger.warn(
            '[useOfflineRecordingEvents] synced transcript invalidation failed',
            err
          );
        });
      }
    };

    // offline_recording:recovered — boot-time crash recovery (T056, APP-6363).
    // Emitted once after run_recovery() during Tauri setup. On a cold boot
    // the WebView may not be mounted yet, so this event is not guaranteed to
    // arrive here — list_sync_queue (US2) is the durable hydration path.
    const onRecovered = (e: Event<RecoveredPayload>) => {
      const recoveredSessionIds = e.payload.recoveredSessionIds.filter(
        (localSessionId) => !isDeletedLocalSession(localSessionId)
      );
      if (recoveredSessionIds.length === 0) return;
      logger.info({
        msg: '[offline_recording] crash recovery: sessions salvaged on boot',
        count: recoveredSessionIds.length,
        sessionIds: recoveredSessionIds,
      });
      // mergeRecoveredItems builds placeholder rows; list_sync_queue hydrates full data.
      setQueue((prev) => mergeRecoveredItems(prev, recoveredSessionIds));
    };

    // Rust emits these two app-wide (`app.emit` broadcasts to every webview) and
    // this hook is mounted unconditionally in `_app.tsx`, so without a gate the
    // overlay and recording-toolbar windows each render their own copy of the
    // notice — one unplug, three toasts. `ipc.ts` guarded its old toast the same
    // way; moving the notification here moved the hazard with it.
    //
    // Deliberately scoped to the toasts and to the idle-stream handover on
    // `capture_ended` — both are app-wide effects. The logging stays in every
    // window (it is per-webview diagnostics), and no atom-mutating handler in
    // this hook is gated — those must keep running everywhere or the queue
    // state diverges between windows.

    // offline_recording:input_recovered — the microphone was unplugged
    // mid-session and capture continued on another one.
    //
    // `warning`, not `info`: the replacement is usually the built-in
    // microphone, which may transcribe worse than the headset that dropped.
    // That is something the clinician should notice, not something to bury —
    // the online path makes the same call in `audio-device-lost-recovery.tsx`.
    const onInputRecovered = (e: Event<InputRecoveredPayload>) => {
      const { localSessionId, lostDevice, deviceName } = e.payload;
      logger.warn({
        msg: '[offline_recording] input device lost mid-capture; continued on another device',
        localSessionId,
        lostDevice,
        deviceName,
      });
      if (!isMainWindow()) return;
      toast({
        tone: 'warning',
        title: intlRef.current.formatMessage(offlineDeviceLostMessages.recoveredTitle, {
          device: lostDevice,
        }),
        description: intlRef.current.formatMessage(
          offlineDeviceLostMessages.recoveredDescription,
          { device: deviceName }
        ),
      });
    };

    // offline_recording:input_recovery_failed — the microphone was unplugged
    // and nothing could take over. The capture is still running and padding
    // silence, so the honest advice is to end the session and keep what was
    // captured before the unplug.
    const onInputRecoveryFailed = (e: Event<InputRecoveryFailedPayload>) => {
      const { localSessionId, lostDevice, reason } = e.payload;
      logger.warn({
        msg: '[offline_recording] input device lost mid-capture and could not be replaced',
        localSessionId,
        lostDevice,
        reason,
      });
      if (!isMainWindow()) return;
      toast({
        tone: 'destructive',
        title: intlRef.current.formatMessage(offlineDeviceLostMessages.title),
        description: intlRef.current.formatMessage(
          reason === 'no_input_device'
            ? offlineDeviceLostMessages.description
            : offlineDeviceLostMessages.errorDescription
        ),
        duration: 10000,
      });
    };

    // offline_recording:deleted — a recording was purged, either manually
    // (FR-015) or by the retention sweep (FR-033, APP-6667). Drop it from the
    // in-memory queue so the count/list reflect the removal.
    //
    // Like `recovered`, the boot-time retention sweep fires during Tauri setup
    // before the WebView mounts, so this listener is not guaranteed to receive
    // the cold-boot purge — list_sync_queue (US2) is the durable path (purged
    // rows are simply absent). The listener still matters for non-setup
    // emitters (manual delete, any future periodic sweep).
    const onDeleted = (e: Event<DeletedPayload>) => {
      const { localSessionId, reason } = e.payload;
      const queuedItem = findQueuedItem(queueRef.current, localSessionId);
      trackOfflineRecordingDeleted({
        local_session_id: localSessionId,
        deletion_source: reason,
        status: queuedItem?.status ?? 'unknown',
        attempt_count: queuedItem?.attemptCount ?? 0,
        source: OFFLINE_RECORDING_ANALYTICS_SOURCE,
      });
      deletedLocalSessionIdsRef.current.add(localSessionId);
      // A recording deleted while its tick is still up has nothing left to
      // dismiss. The only cancel on the delete path: `onDeleted` clears the row
      // itself rather than going through `dropDeletedLocalSession`.
      cancelSyncedDismissal(localSessionId);
      setOfflineDeletedLocalSessionIds((prev) =>
        appendUniqueSessionId(prev, localSessionId)
      );
      setOfflineLocalSessions((prev) => removeLocalSession(prev, localSessionId));
      setQueue((prev) => removeQueuedSession(prev, localSessionId));
      setNoteFailedSessionIds((prev) => removeNoteFailedSession(prev, localSessionId));
      setNoteFailedDismissedSessionIds((prev) =>
        removeNoteFailedSession(prev, localSessionId)
      );
    };

    // offline_recording:server_delete_enqueued — the clinician deleted one or
    // more server-backed sessions while offline and Rust has committed the
    // intent. Union the ids into the render-time mirror so every list surface
    // in this window stops showing them.
    //
    // Runs in EVERY webview, like every other atom-mutating handler here: Rust
    // broadcasts the event app-wide and each webview keeps its own mirror, so
    // gating this would leave a second window still showing a deleted session.
    //
    // KNOWN GAP, deliberately not closed here. The event carries ids but not
    // the clinician they were queued for, so a union cannot be attributed: an
    // event emitted for one clinician and handled just after another signs in
    // puts the first one's ids into the second one's mirror, which persists
    // them. The durable read below is scope-stamped and cannot do this; only
    // the broadcast can.
    //
    // The obvious fix — confirm every union with a durable read — was tried and
    // withdrawn, because it breaks a guarantee that is also load-bearing: an
    // enqueue landing mid-read must SURVIVE the reply, or a delete made in a
    // second window is erased and its session flashes back. A confirming read
    // snapshots the mirror after the union, so it drops exactly the id the
    // mid-read rule exists to keep. The two cannot both be served from ids
    // alone. Closing this properly means putting the owning user on the event,
    // which is the Rust command's contract and not this slice's to change.
    const onServerDeleteEnqueued = (e: Event<OfflineServerDeleteEnqueuedEvent>) => {
      setPendingServerDeleteIds((prev) =>
        unionServerSessionIds(prev, e.payload.serverSessionIds)
      );
    };

    // Rust emits one settled event per row, so a drain that gives up on several
    // deletes at once would otherwise fire several identical toasts. Collect
    // them into one: the clinician is owed the fact that a deletion did not
    // happen and that the sessions are back, and repeating that per row tells
    // them nothing further. The copy is deliberately count-free and title-free.
    let deleteFailedToastTimer: ReturnType<typeof setTimeout> | undefined;
    const queueDeleteFailedToast = () => {
      if (deleteFailedToastTimer !== undefined) return;
      deleteFailedToastTimer = setTimeout(() => {
        deleteFailedToastTimer = undefined;
        const message = offlineRecordingErrorMessages.deleteFailed;
        toast({
          tone: 'destructive',
          title: intlRef.current.formatMessage(message.title),
          description: intlRef.current.formatMessage(message.description),
        });
      }, DELETE_FAILED_TOAST_COALESCE_MS);
    };

    // offline_recording:server_delete_settled — a queued delete stopped being
    // owed, one way or the other. Exactly one id per event.
    const onServerDeleteSettled = (e: Event<OfflineServerDeleteSettledEvent>) => {
      const { serverSessionId, outcome, errorCode } = e.payload;
      const dropId = () => {
        setPendingServerDeleteIds((prev) =>
          withoutServerSessionId(prev, serverSessionId)
        );
      };

      if (outcome === 'replayed') {
        // The backend has the delete, but this renderer's list cache does not
        // know that yet. Dropping the id first would un-hide the session for
        // the whole duration of the refetch — a row reappearing and vanishing
        // again is worse than the row simply never being there. So: invalidate,
        // wait, THEN stop compensating. A failed invalidation still drops the
        // id: the mirror's job is over either way, and holding an id nothing
        // will ever settle again would hide the session forever.
        void invalidateSessionListCaches()
          .catch((err: unknown) => {
            logger.warn(
              '[useOfflineRecordingEvents] queued-delete sessions invalidation failed',
              err
            );
          })
          .finally(dropId);
        return;
      }

      // The delete will never happen. The session comes back into the list and
      // the clinician is told, because the alternative is a session they
      // believe is deleted quietly existing everywhere else. Its local audio is
      // untouched, so nothing has been lost.
      dropId();
      logger.warn({
        msg: '[offline_recording] queued session delete given up',
        // Opaque identifiers and an error code only — no title, patient link or
        // note text may reach a log line on this path. The code is what
        // separates a genuine permission refusal from a proxy or a spent retry
        // budget after the fact; they wear the same copy on purpose.
        serverSessionId,
        errorCode,
      });
      if (!isMainWindow()) return;
      // Main-window only, and per row rather than per toast: the toast is
      // coalesced because repeating it tells the clinician nothing, but the
      // funnel needs to count every delete that was given up, and every
      // window seeing the broadcast would otherwise multiply the count.
      trackOfflineSessionDeleteReplayFailed({
        error_code: toTelemetryErrorCode(errorCode),
        source: 'offline_recording',
      });
      queueDeleteFailedToast();
    };

    const setup = async () => {
      // Registers a listener and pushes the unlisten fn, or cleans up and
      // returns false if the effect was already cancelled during the await.
      const register = async <T>(
        eventName: string,
        handler: (e: Event<T>) => void
      ): Promise<boolean> => {
        const fn = await listen<T>(eventName, handler);
        if (cancelled) {
          fn();
          return false;
        }
        unlistenFns.push(fn);
        return true;
      };

      if (!(await register('offline_recording:capture_started', onCaptureStarted)))
        return;
      if (
        !(await register<CaptureEndedPayload>(
          'offline_recording:capture_ended',
          onCaptureEnded
        ))
      )
        return;
      if (
        !(await register<PendingSyncPayload>(
          'offline_recording:pending_sync',
          onPendingSync
        ))
      )
        return;
      if (!(await register(OFFLINE_CONNECTIVITY_CHANGED_EVENT, onConnectivityChanged)))
        return;
      if (
        !(await register<OfflineStatusChangedPayload>(
          'offline_recording:status_changed',
          onStatusChanged
        ))
      )
        return;
      if (
        !(await register<OfflineNoteFailedPayload>(
          'offline_recording:note_failed',
          onNoteFailed
        ))
      )
        return;
      if (!(await register<OfflineSyncedPayload>('offline_recording:synced', onSynced)))
        return;
      if (
        !(await register<InputRecoveredPayload>(
          'offline_recording:input_recovered',
          onInputRecovered
        ))
      )
        return;
      if (
        !(await register<InputRecoveryFailedPayload>(
          'offline_recording:input_recovery_failed',
          onInputRecoveryFailed
        ))
      )
        return;
      if (!(await register('offline_recording:recovered', onRecovered))) return;
      if (!(await register<DeletedPayload>('offline_recording:deleted', onDeleted)))
        return;
      if (
        !(await register<OfflineServerDeleteEnqueuedEvent>(
          OFFLINE_SERVER_DELETE_ENQUEUED_EVENT,
          onServerDeleteEnqueued
        ))
      )
        return;
      if (
        !(await register<OfflineServerDeleteSettledEvent>(
          OFFLINE_SERVER_DELETE_SETTLED_EVENT,
          onServerDeleteSettled
        ))
      )
        return;
      // The only hydration nothing else re-triggers, so it is the only one that
      // retries. The event-driven refreshes above each have another event behind
      // them; this one is the whole of what a cold boot knows.
      void refreshQueueFromBridge('initial', HYDRATION_RETRY_DELAYS_MS.length);
      // Beside it, and for the same reason: nothing else re-reads the durable
      // delete queue on a cold boot. Note the ordering against the mirror's own
      // synchronous restore — that has already happened, during the first
      // render, which is what the first-frame criterion depends on. This read
      // only reconciles it.
      void refreshPendingServerDeletes('initial', HYDRATION_RETRY_DELAYS_MS.length);
    };

    void setup();

    return () => {
      cancelled = true;
      refreshPendingServerDeletesRef.current = () => {};
      unregisterQueueRefresh();
      unsubscribeQueryCache();
      // So no pending callback outlives the renderer and sets an atom on it.
      syncedDismissalTimers.forEach((timer) => clearTimeout(timer));
      syncedDismissalTimers.clear();
      unlistenFns.forEach((fn) => fn());
      hydrationRetryTimers.forEach((timer) => clearTimeout(timer));
      if (deleteFailedToastTimer !== undefined) clearTimeout(deleteFailedToastTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-read the durable delete queue whenever the user-scoped storage bucket
  // changes underneath the mirror. Separate from the listener effect on
  // purpose: that one is mount-once (re-registering every Tauri listener on a
  // sign-in would be a leak), while this has to run again each time the scope
  // moves. Skips its own first run — the initial hydration above already
  // covers the scope the app booted with.
  useEffect(() => {
    if (appliedRehydrationSignalRef.current === userScopeRehydrationSignal) return;
    appliedRehydrationSignalRef.current = userScopeRehydrationSignal;
    refreshPendingServerDeletesRef.current('user_scope_changed');
  }, [userScopeRehydrationSignal]);
}
